import React from 'react';
import Box from '../Box/Box';
import TPillBase from './types/TPillBase';
import Icon from '../Icon/Icon';
import filterConditionalKeyValues from '../../utils/filterConditionalKeyValues';
import keyboardKeys from '../../constants/keyboardKeys';
import getComponentClassNames from '../../utils/className/getComponentClassNames';

export const classNames = getComponentClassNames(['Pill'], ['startIcon', 'endIcon']);

const { ENTER, SPACE } = keyboardKeys;

type TBaseProps = {
  as: 'button';
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const PillBase: React.FC<TPillBase> = ({
  children,
  kind = '',
  onClick,
  isActive = false,
  isCloseable = false,
  icon,
  translations: { ariaLabelDefault, ariaLabelActive } = {},
  ...rest
}) => {
  if (!(ariaLabelActive && ariaLabelDefault)) {
    throw new Error('Pill requires ariaLabelActive and ariaLabelDefault props');
  }

  const handleClickOrKeyDown = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    onClick?.(e);
  };

  const baseProps: TBaseProps = {
    as: 'button',
    onClick: handleClickOrKeyDown,
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ENTER || e.key === SPACE) {
      e.preventDefault();
      handleClickOrKeyDown(e);
    }
  };

  // only active pills are closable
  const _isClosable = isCloseable && isActive;

  const conditionalProps = filterConditionalKeyValues({
    modifiers: [kind, icon && 'iconStart', _isClosable && 'iconEnd'],
    states: isActive && 'active',
  });

  const ariaLabelPrefix = typeof children === 'string' ? `${children}, ` : '';

  return (
    <Box
      {...rest}
      {...baseProps}
      {...conditionalProps}
      name="Pill"
      onKeyDown={handleKeyDown}
      aria-label={`${ariaLabelPrefix}${isActive ? ariaLabelActive : ariaLabelDefault}`}
      aria-pressed={isActive}
    >
      {icon && <Icon className={classNames.startIcon} name={icon} />}
      {children}
      {_isClosable && <Icon className={classNames.endIcon} name="close-small" />}
    </Box>
  );
};

export default PillBase;
